import { useRef, useEffect, useCallback } from 'react';
export const useDebouncedUpdate = (updateCallback, delay = 150) => {
  const timerRef = useRef(null);
  const updateCallbackRef = useRef(updateCallback);

  // keep callback ref up to date
  useEffect(() => {
    updateCallbackRef.current = updateCallback;
  }, [updateCallback]);

  // debounced update function
  const debouncedUpdate = useCallback((...args) => {
    // clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // set new timer
    timerRef.current = setTimeout(() => {
      updateCallbackRef.current(...args);
      timerRef.current = null;
    }, delay);
  }, [delay]);

  // flush pending updates immediately
  const flushPendingUpdates = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // cancel pending updates
  const cancelPendingUpdates = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    debouncedUpdate,
    flushPendingUpdates,
    cancelPendingUpdates
  };
};

